import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  CheckCircle2,
  AlertTriangle,
  XCircle,
  RefreshCw,
  ExternalLink,
  ShieldCheck,
  Folder,
  Layers,
  Settings,
  HelpCircle,
  Clock,
  User,
  Sliders,
  Unlink,
  FileCheck,
  Video,
  ListVideo,
  EyeOff,
  Baby,
  Users,
  Info,
} from 'lucide-react';
import { EduCard } from '../components/EduCard.js';
import { EduButton } from '../components/EduButton.js';
import { EduBadge } from '../components/EduBadge.js';
import { api } from '../services/api.js';
import { YouTubeConnection, YouTubeConnectionStatus, YouTubeTransferSettings } from '../types/index.js';

export const YouTubeIntegration: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [connection, setConnection] = useState<YouTubeConnection | null>(null);
  const [loading, setLoading] = useState(true);
  const [testing, setTesting] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);

  // Settings form state
  const [defaultLanguage, setDefaultLanguage] = useState('pt-BR');
  const [defaultCategoryId, setDefaultCategoryId] = useState('27');
  const [titleTemplate, setTitleTemplate] = useState('{numeroAula} - {tituloAula}');
  const [descriptionTemplate, setDescriptionTemplate] = useState(
    'Projeto: {nomeProjeto}\n\nAula: {tituloAula}\n\nDescrição:\n{descricaoAula}'
  );
  const [defaultAudienceClassification, setDefaultAudienceClassification] = useState(false);
  const [uploadConcurrency, setUploadConcurrency] = useState(1);
  const [settingsSaved, setSettingsSaved] = useState(false);

  const loadIntegration = async () => {
    try {
      setLoading(true);
      const data = await api.getYouTubeIntegration();
      setConnection(data);
      if (data.settings) {
        setDefaultLanguage(data.settings.defaultLanguage || 'pt-BR');
        setDefaultCategoryId(data.settings.defaultCategoryId || '27');
        setTitleTemplate(data.settings.titleTemplate || '{numeroAula} - {tituloAula}');
        setDescriptionTemplate(
          data.settings.descriptionTemplate ||
            'Projeto: {nomeProjeto}\n\nAula: {tituloAula}\n\nDescrição:\n{descricaoAula}'
        );
        setDefaultAudienceClassification(Boolean(data.settings.defaultAudienceClassification));
        setUploadConcurrency(data.settings.uploadConcurrency || 1);
      }
    } catch (err) {
      console.error('Erro ao carregar integração do YouTube:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadIntegration();
    if (searchParams.get('status') === 'connected') {
      setTestResult({
        success: true,
        message: 'Canal do YouTube conectado com sucesso! Clique em "Testar conexão" para validar permissões de upload.',
      });
    }
    const errorParam = searchParams.get('error');
    if (errorParam) {
      setTestResult({
        success: false,
        message: decodeURIComponent(errorParam),
      });
    }
  }, [searchParams]);

  const handleConnect = async () => {
    try {
      const redirectUrl = `${window.location.origin}/minha-conta?tab=youtube`;
      const res = await api.connectYouTube(redirectUrl);
      if (res.authUrl) {
        window.location.href = res.authUrl;
      }
    } catch (err: any) {
      alert(err.message || 'Falha ao iniciar conexão com o YouTube.');
    }
  };

  const handleTestConnection = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const res = await api.testYouTubeConnection();
      setTestResult(res);
      await loadIntegration();
    } catch (err: any) {
      setTestResult({
        success: false,
        message: err.message || 'Falha ao validar canal do YouTube.',
      });
      await loadIntegration();
    } finally {
      setTesting(false);
    }
  };

  const handleSaveSettings = async (e: React.FormEvent) => {
    e.preventDefault();
    setSavingSettings(true);
    try {
      const updated = await api.updateYouTubeSettings({
        defaultLanguage,
        defaultCategoryId,
        titleTemplate: titleTemplate.trim() || '{numeroAula} - {tituloAula}',
        descriptionTemplate: descriptionTemplate.trim(),
        defaultAudienceClassification,
        uploadConcurrency,
      });
      if (connection) {
        setConnection({ ...connection, settings: updated });
      }
      setSettingsSaved(true);
      setTimeout(() => setSettingsSaved(false), 3000);
    } catch (err: any) {
      alert(err.message || 'Falha ao salvar configurações do YouTube.');
    } finally {
      setSavingSettings(false);
    }
  };

  const handleDisconnect = async () => {
    if (
      !confirm(
        'Deseja realmente desconectar seu canal do YouTube? Novas transferências para este canal serão bloqueadas.'
      )
    ) {
      return;
    }
    try {
      setLoading(true);
      await api.disconnectYouTube();
      setTestResult(null);
      await loadIntegration();
    } catch (err: any) {
      alert(err.message || 'Falha ao desconectar canal do YouTube.');
    } finally {
      setLoading(false);
    }
  };

  const isConnected =
    connection &&
    connection.connectionStatus !== 'not_configured' &&
    connection.connectionStatus !== 'disconnected';

  const isValid = connection?.connectionStatus === 'valid';

  const getStatusBadge = (status?: YouTubeConnectionStatus) => {
    switch (status) {
      case 'valid':
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-green-50 text-status-success border border-green-200 shadow-xs">
            <CheckCircle2 size={14} /> Conexão Válida
          </span>
        );
      case 'connected':
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-blue-50 text-status-info border border-blue-200">
            <CheckCircle2 size={14} /> Conectado (Pendente Teste)
          </span>
        );
      case 'validating':
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-amber-50 text-amber-700 border border-amber-200">
            <RefreshCw size={14} className="animate-spin" /> Validando Canal...
          </span>
        );
      case 'insufficient_permissions':
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-amber-50 text-amber-700 border border-amber-200">
            <AlertTriangle size={14} /> Permissão Insuficiente
          </span>
        );
      case 'expired':
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-red-50 text-status-error border border-red-200">
            <AlertTriangle size={14} /> Autorização Expirada
          </span>
        );
      case 'error':
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-red-50 text-status-error border border-red-200">
            <XCircle size={14} /> Erro na Conexão
          </span>
        );
      case 'disconnected':
      case 'not_configured':
      default:
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-slate-100 text-txt-secondary border border-slate-200">
            <AlertTriangle size={14} className="text-txt-muted" /> Não Configurado
          </span>
        );
    }
  };

  return (
    <div className="space-y-6 max-w-5xl">
      {/* 3.1 CARD PRINCIPAL DA INTEGRAÇÃO */}
      <div className="bg-surface border border-bdr rounded-2xl p-6 shadow-sm">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6 pb-6 border-b border-bdr">
          {/* Logo do YouTube e Título */}
          <div className="flex items-start gap-4">
            <div className="w-14 h-14 rounded-2xl bg-red-50 border border-red-100 flex items-center justify-center shrink-0 shadow-xs">
              {/* YouTube Official Logo SVG */}
              <svg className="w-9 h-9 text-[#FF0000]" viewBox="0 0 24 24" fill="currentColor">
                <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
              </svg>
            </div>
            <div>
              <div className="flex items-center gap-3 flex-wrap">
                <h2 className="text-xl font-bold text-txt-primary">YouTube</h2>
                {getStatusBadge(connection?.connectionStatus)}
              </div>
              <p className="text-xs text-txt-secondary mt-1 max-w-xl leading-relaxed">
                Exporte videoaulas concluídas diretamente para o seu canal do YouTube.
                Cada projeto EduRender é organizado em uma playlist não listada com vídeos estritamente privados.
              </p>
            </div>
          </div>

          {/* 3.2 BOTÕES DE AÇÃO */}
          <div className="flex items-center gap-2 flex-wrap w-full md:w-auto justify-end">
            {!isConnected ? (
              <EduButton
                variant="primary"
                size="md"
                onClick={handleConnect}
                disabled={loading}
                className="bg-[#FF0000] hover:bg-[#CC0000] text-white border-none shadow-sm"
                icon={<Video size={16} />}
              >
                Conectar Canal do YouTube
              </EduButton>
            ) : (
              <>
                <EduButton
                  variant="secondary"
                  size="sm"
                  onClick={handleTestConnection}
                  disabled={testing}
                  icon={<RefreshCw size={14} className={testing ? 'animate-spin' : ''} />}
                  title="Executar verificação real na YouTube Data API"
                >
                  {testing ? 'Testando...' : 'Testar Conexão'}
                </EduButton>

                <EduButton
                  variant="secondary"
                  size="sm"
                  onClick={handleConnect}
                  disabled={loading}
                  icon={<ExternalLink size={14} />}
                >
                  Reconectar
                </EduButton>

                <button
                  type="button"
                  onClick={handleDisconnect}
                  disabled={loading}
                  className="p-2 text-txt-muted hover:text-status-error hover:bg-red-50 rounded-lg transition-colors border border-transparent hover:border-red-200"
                  title="Desconectar canal do YouTube"
                >
                  <Unlink size={16} />
                </button>
              </>
            )}
          </div>
        </div>

        {/* Feedback de Teste */}
        {testResult && (
          <div
            className={`mt-4 p-4 rounded-xl border flex items-start gap-3 text-xs animate-in fade-in duration-200 ${
              testResult.success
                ? 'bg-green-50/80 border-green-200 text-green-900'
                : 'bg-red-50/80 border-red-200 text-red-900'
            }`}
          >
            {testResult.success ? (
              <CheckCircle2 size={18} className="text-status-success shrink-0 mt-0.5" />
            ) : (
              <AlertTriangle size={18} className="text-status-error shrink-0 mt-0.5" />
            )}
            <div className="flex-1">
              <p className="font-bold text-sm">
                {testResult.success ? 'Diagnóstico de Conexão: Sucesso' : 'Falha na Conexão'}
              </p>
              <p className="mt-0.5 leading-relaxed">{testResult.message}</p>
            </div>
            <button
              onClick={() => setTestResult(null)}
              className="text-txt-muted hover:text-txt-primary p-1"
            >
              ×
            </button>
          </div>
        )}

        {/* Detalhes do Canal Conectado */}
        {isConnected && connection && (
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Canal do YouTube */}
            <div className="bg-surface-secondary border border-bdr rounded-xl p-4 flex items-center gap-3.5">
              {connection.channelThumbnail ? (
                <img
                  src={connection.channelThumbnail}
                  alt={connection.channelTitle}
                  className="w-11 h-11 rounded-full object-cover border border-bdr shrink-0"
                />
              ) : (
                <div className="w-11 h-11 rounded-full bg-red-100 text-[#FF0000] flex items-center justify-center font-bold text-sm shrink-0">
                  YT
                </div>
              )}
              <div className="min-w-0 flex-1">
                <p className="text-[11px] font-semibold text-txt-muted">Canal Conectado</p>
                <p className="text-sm font-bold text-txt-primary truncate" title={connection.channelTitle}>
                  {connection.channelTitle}
                </p>
                {connection.channelCustomUrl && (
                  <p className="text-[11px] font-mono text-txt-secondary truncate">
                    {connection.channelCustomUrl}
                  </p>
                )}
              </div>
            </div>

            {/* Identificador do Canal */}
            <div className="bg-surface-secondary border border-bdr rounded-xl p-4 flex flex-col justify-center">
              <p className="text-[11px] font-semibold text-txt-muted">Channel ID</p>
              <p className="text-xs font-mono font-bold text-txt-primary truncate mt-0.5" title={connection.channelId}>
                {connection.channelId}
              </p>
              <a
                href={`https://www.youtube.com/channel/${connection.channelId}`}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 text-[11px] font-semibold text-brand-primary hover:underline mt-1"
              >
                Abrir Canal <ExternalLink size={11} />
              </a>
            </div>

            {/* Data e Último Teste */}
            <div className="bg-surface-secondary border border-bdr rounded-xl p-4 flex flex-col justify-center">
              <p className="text-[11px] font-semibold text-txt-muted">Último Teste</p>
              <p className="text-xs font-bold text-txt-primary mt-0.5">
                {connection.lastTestAt
                  ? new Date(connection.lastTestAt).toLocaleString('pt-BR')
                  : 'Pendente'}
              </p>
              <span className="text-[11px] text-txt-muted truncate mt-1">
                Resultado: {connection.lastTestStatus === 'success' ? 'Válido' : 'Necessita atenção'}
              </span>
            </div>

            {/* Atalho para Transferências */}
            <div className="bg-surface-secondary border border-bdr rounded-xl p-4 flex flex-col justify-between">
              <div>
                <p className="text-[11px] font-semibold text-txt-muted">Fila de Transferências</p>
                <p className="text-xs text-txt-secondary mt-0.5">
                  Acompanhe o envio de aulas em tempo real
                </p>
              </div>
              <EduButton
                variant="secondary"
                size="sm"
                className="w-full mt-2"
                onClick={() => navigate('/transferencias?provider=youtube')}
                icon={<ListVideo size={13} />}
              >
                Consultar Transferências
              </EduButton>
            </div>
          </div>
        )}
      </div>

      {/* REGRAS FIXAS DE EXPORTAÇÃO (SEÇÃO 7 e 9) */}
      <div className="bg-surface border border-bdr rounded-2xl p-6 shadow-sm space-y-4">
        <div className="flex items-center gap-2.5 pb-2 border-b border-bdr">
          <ShieldCheck size={20} className="text-brand-primary" />
          <h3 className="text-base font-bold text-txt-primary">Diretrizes Fixas de Privacidade & Segurança</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 flex items-start gap-3">
            <EyeOff size={20} className="text-brand-primary shrink-0 mt-0.5" />
            <div>
              <p className="text-xs font-bold text-txt-primary">Vídeos Privados (private)</p>
              <p className="text-[11px] text-txt-secondary mt-0.5 leading-relaxed">
                Todas as videoaulas são enviadas exclusivamente com visibilidade <strong>privada</strong>.
                Não são publicados vídeos públicos nem não listados nesta versão.
              </p>
            </div>
          </div>

          <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 flex items-start gap-3">
            <ListVideo size={20} className="text-brand-primary shrink-0 mt-0.5" />
            <div>
              <p className="text-xs font-bold text-txt-primary">Playlist Não Listada (unlisted)</p>
              <p className="text-[11px] text-txt-secondary mt-0.5 leading-relaxed">
                Cada projeto recebe uma playlist própria com visibilidade <strong>não listada</strong>.
                As aulas mantêm a ordem exata cadastrada no EduRender.
              </p>
            </div>
          </div>

          <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 flex items-start gap-3">
            <Info size={20} className="text-brand-primary shrink-0 mt-0.5" />
            <div>
              <p className="text-xs font-bold text-txt-primary">Envio Exclusivamente Manual</p>
              <p className="text-[11px] text-txt-secondary mt-0.5 leading-relaxed">
                Não há exportação automática nem consumo de créditos da sua franquia de aulas.
                O envio utiliza exclusivamente MP4 já finalizados.
              </p>
            </div>
          </div>
        </div>

        {/* Aviso Obrigatório Seção 9 */}
        <div className="p-3.5 bg-amber-50 border border-amber-200 rounded-xl text-xs text-amber-900 flex items-start gap-2.5">
          <AlertTriangle size={16} className="text-amber-700 shrink-0 mt-0.5" />
          <p className="leading-relaxed">
            <strong>Aviso de visualização:</strong> Os vídeos serão enviados como privados. O link da playlist
            não concede automaticamente acesso aos vídeos para outros espectadores. Para que convidados assistam,
            é necessário gerenciar permissões no YouTube Studio ou aguardar atualizações de privacidade controlada.
          </p>
        </div>
      </div>

      {/* SEÇÃO 10: CONFIGURAÇÃO DE PÚBLICO E CLASSIFICAÇÃO INFANTIL */}
      <div className="bg-surface border border-bdr rounded-2xl p-6 shadow-sm space-y-4">
        <div className="flex items-center gap-2.5 pb-2 border-b border-bdr">
          <Baby size={20} className="text-brand-primary" />
          <div>
            <h3 className="text-base font-bold text-txt-primary">Público e Classificação do Conteúdo</h3>
            <p className="text-xs text-txt-secondary">
              Defina o padrão de declaração exigido pela legislação (COPPA) para a API do YouTube
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Opção B: Conteúdo não feito especificamente para crianças */}
          <div
            onClick={() => setDefaultAudienceClassification(false)}
            className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
              !defaultAudienceClassification
                ? 'border-brand-primary bg-orange-50/40 shadow-xs'
                : 'border-bdr hover:border-slate-300 bg-surface'
            }`}
          >
            <div className="flex items-start gap-3">
              <input
                type="radio"
                name="audienceClassification"
                checked={!defaultAudienceClassification}
                onChange={() => setDefaultAudienceClassification(false)}
                className="mt-1 text-brand-primary focus:ring-brand-primary"
              />
              <div>
                <p className="text-sm font-bold text-txt-primary flex items-center gap-2">
                  <Users size={16} className="text-brand-primary" />
                  Conteúdo não feito especificamente para crianças
                </p>
                <p className="text-xs text-txt-secondary mt-1 leading-relaxed">
                  (Recomendado para público geral, universitário, profissional ou preparatório).
                  Aplicável mesmo que o conteúdo seja adequado a menores de idade.
                </p>
                <span className="inline-block mt-2 px-2 py-0.5 rounded text-[10px] font-mono font-semibold bg-slate-100 text-txt-secondary border border-slate-200">
                  status.selfDeclaredMadeForKids = false
                </span>
              </div>
            </div>
          </div>

          {/* Opção A: Feito para crianças */}
          <div
            onClick={() => setDefaultAudienceClassification(true)}
            className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
              defaultAudienceClassification
                ? 'border-brand-primary bg-orange-50/40 shadow-xs'
                : 'border-bdr hover:border-slate-300 bg-surface'
            }`}
          >
            <div className="flex items-start gap-3">
              <input
                type="radio"
                name="audienceClassification"
                checked={defaultAudienceClassification}
                onChange={() => setDefaultAudienceClassification(true)}
                className="mt-1 text-brand-primary focus:ring-brand-primary"
              />
              <div>
                <p className="text-sm font-bold text-txt-primary flex items-center gap-2">
                  <Baby size={16} className="text-brand-primary" />
                  Conteúdo feito para crianças
                </p>
                <p className="text-xs text-txt-secondary mt-1 leading-relaxed">
                  Aplicável apenas quando as videoaulas forem <strong>especificamente direcionadas</strong>{' '}
                  à educação infantil ou crianças pequenas.
                </p>
                <span className="inline-block mt-2 px-2 py-0.5 rounded text-[10px] font-mono font-semibold bg-slate-100 text-txt-secondary border border-slate-200">
                  status.selfDeclaredMadeForKids = true
                </span>
              </div>
            </div>
          </div>
        </div>

        <p className="text-xs text-txt-muted leading-relaxed">
          * Você também poderá personalizar a classificação de cada projeto ou aula individualmente no modal de
          confirmação de envio.
        </p>
      </div>

      {/* SEÇÃO 7: PREFERÊNCIAS EDITÁVEIS DE EXPORTAÇÃO */}
      <EduCard title="Preferências de Exportação & Modelos">
        <form onSubmit={handleSaveSettings} className="space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label className="block text-xs font-semibold text-txt-primary mb-1">
                Idioma Padrão dos Vídeos
              </label>
              <select
                value={defaultLanguage}
                onChange={(e) => setDefaultLanguage(e.target.value)}
                className="w-full px-3 py-2 text-sm bg-surface border border-bdr rounded-lg text-txt-primary focus:outline-none focus:border-brand-primary"
              >
                <option value="pt-BR">Português (Brasil)</option>
                <option value="en">Inglês</option>
                <option value="es">Espanhol</option>
              </select>
            </div>

            <div>
              <label className="block text-xs font-semibold text-txt-primary mb-1">
                Categoria no YouTube
              </label>
              <select
                value={defaultCategoryId}
                onChange={(e) => setDefaultCategoryId(e.target.value)}
                className="w-full px-3 py-2 text-sm bg-surface border border-bdr rounded-lg text-txt-primary focus:outline-none focus:border-brand-primary"
              >
                <option value="27">Educação (Categoria Padrão)</option>
                <option value="28">Ciência e Tecnologia</option>
                <option value="22">Pessoas e Blogs</option>
              </select>
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="block text-xs font-semibold text-txt-primary">
                Modelo de Título do Vídeo
              </label>
              <span className="text-[11px] text-txt-muted">Máximo 100 caracteres</span>
            </div>
            <input
              type="text"
              value={titleTemplate}
              onChange={(e) => setTitleTemplate(e.target.value)}
              placeholder="{numeroAula} - {tituloAula}"
              className="w-full px-3 py-2 text-sm bg-surface border border-bdr rounded-lg text-txt-primary font-mono focus:outline-none focus:border-brand-primary"
            />
            <div className="flex items-center gap-2 mt-1.5 flex-wrap">
              <span className="text-[11px] text-txt-muted font-medium">Tags disponíveis:</span>
              <button
                type="button"
                onClick={() => setTitleTemplate((prev) => prev + ' {numeroAula}')}
                className="text-[10px] font-mono px-2 py-0.5 rounded bg-surface-secondary border border-bdr text-brand-primary hover:bg-orange-50"
              >
                {'{numeroAula}'}
              </button>
              <button
                type="button"
                onClick={() => setTitleTemplate((prev) => prev + ' {tituloAula}')}
                className="text-[10px] font-mono px-2 py-0.5 rounded bg-surface-secondary border border-bdr text-brand-primary hover:bg-orange-50"
              >
                {'{tituloAula}'}
              </button>
              <button
                type="button"
                onClick={() => setTitleTemplate((prev) => prev + ' {nomeProjeto}')}
                className="text-[10px] font-mono px-2 py-0.5 rounded bg-surface-secondary border border-bdr text-brand-primary hover:bg-orange-50"
              >
                {'{nomeProjeto}'}
              </button>
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="block text-xs font-semibold text-txt-primary">
                Modelo de Descrição do Vídeo
              </label>
              <span className="text-[11px] text-txt-muted">Máximo 5000 caracteres</span>
            </div>
            <textarea
              rows={4}
              value={descriptionTemplate}
              onChange={(e) => setDescriptionTemplate(e.target.value)}
              className="w-full px-3 py-2 text-sm bg-surface border border-bdr rounded-lg text-txt-primary font-mono focus:outline-none focus:border-brand-primary resize-none leading-relaxed"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 pt-2 border-t border-bdr">
            <div>
              <label className="block text-xs font-semibold text-txt-primary mb-1">
                Política para Aulas Já Enviadas
              </label>
              <input
                type="text"
                disabled
                value="Não reenviar vídeos idênticos já enviados (Idempotência)"
                className="w-full px-3 py-2 text-xs bg-surface-secondary border border-bdr rounded-lg text-txt-secondary cursor-not-allowed"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-txt-primary mb-1">
                Concorrência Máxima de Upload
              </label>
              <select
                value={uploadConcurrency}
                onChange={(e) => setUploadConcurrency(Number(e.target.value))}
                className="w-full px-3 py-2 text-sm bg-surface border border-bdr rounded-lg text-txt-primary focus:outline-none focus:border-brand-primary"
              >
                <option value={1}>1 upload simultâneo por canal (Padrão Recomendado)</option>
                <option value={2}>2 uploads simultâneos</option>
              </select>
            </div>
          </div>

          <div className="flex items-center justify-between pt-4 border-t border-bdr">
            {settingsSaved && (
              <span className="text-xs text-status-success font-medium flex items-center gap-1.5 animate-in fade-in">
                <CheckCircle2 size={16} /> Configurações do YouTube salvas com sucesso!
              </span>
            )}
            <EduButton
              type="submit"
              variant="primary"
              size="sm"
              isLoading={savingSettings}
              className="ml-auto"
            >
              Salvar Preferências
            </EduButton>
          </div>
        </form>
      </EduCard>
    </div>
  );
};
